// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Button,
  Tile,
  Tag,
  Modal,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  TimePicker,
  TimePickerSelect,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  InlineNotification,
  Loading,
  OverflowMenu,
  OverflowMenuItem,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  NumberInput,
  Checkbox,
  DatePicker,
  DatePickerInput,
  MultiSelect,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListRow,
  StructuredListCell,
  StructuredListBody,
  ProgressIndicator,
  ProgressStep,
} from '@carbon/react';
import {
  UserMultiple,
  Add,
  Calendar,
  Time,
  Email,
  Edit,
  Delete,
  CheckmarkFilled,
  WarningFilled,
  Information,
  DocumentExport,
  Chat,
  Events,
  UserActivity,
} from '@carbon/icons-react';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface Class {
  class_id: string;
  name: string;
  subject: string;
  grade_level: string;
  room_number: string;
  schedule: Schedule[];
  student_count: number;
  max_capacity: number;
  status: 'active' | 'inactive' | 'archived';
  term: string;
  start_date: string;
  end_date: string;
  created_at: string;
}

interface Schedule {
  day: string;
  start_time: string;
  end_time: string;
}

interface Student {
  student_id: string;
  name: string;
  email: string;
  grade_level: string;
  enrollment_date: string;
  attendance_percentage: number;
  grade_average: number;
  parent_email: string;
  status: 'active' | 'inactive' | 'withdrawn';
}

interface Attendance {
  attendance_id: string;
  student_id: string;
  student_name: string;
  date: string;
  status: 'present' | 'absent' | 'late' | 'excused';
  notes: string;
}

interface Announcement {
  announcement_id: string;
  title: string;
  content: string;
  type: 'general' | 'homework' | 'event' | 'urgent';
  created_at: string;
  send_to_parents: boolean;
}

export const TeacherClassManagement: React.FC = () => {
  const { user, token } = useAuthStore();
  const [classes, setClasses] = useState<Class[]>([]);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [attendance, setAttendance] = useState<Attendance[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);

  // Form states
  const [classForm, setClassForm] = useState({
    name: '',
    subject: 'math',
    grade_level: '9',
    room_number: '',
    max_capacity: 30,
    term: 'Fall 2025',
    start_date: '',
    end_date: '',
    schedule: [{ day: 'Monday', start_time: '09:00', end_time: '10:00' }],
  });

  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    type: 'general',
    send_to_parents: false,
  });

  // Load data on mount
  useEffect(() => {
    loadClasses();
  }, [token]);

  useEffect(() => {
    if (selectedClass) {
      loadClassStudents(selectedClass.class_id);
      loadAttendance(selectedClass.class_id, attendanceDate);
      loadAnnouncements(selectedClass.class_id);
    }
  }, [selectedClass, attendanceDate]);

  const loadClasses = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/teacher/classes`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setClasses(response.data.classes || []);
      }
    } catch (err) {
      console.error('Failed to load classes:', err);
      // Mock data for demonstration
      const mockClasses: Class[] = [
        {
          class_id: '1',
          name: 'Math 101 - Period 1',
          subject: 'Mathematics',
          grade_level: '9',
          room_number: 'Room 203',
          schedule: [
            { day: 'Monday', start_time: '09:00', end_time: '10:00' },
            { day: 'Wednesday', start_time: '09:00', end_time: '10:00' },
            { day: 'Friday', start_time: '09:00', end_time: '10:00' },
          ],
          student_count: 25,
          max_capacity: 30,
          status: 'active',
          term: 'Fall 2025',
          start_date: '2025-09-01',
          end_date: '2025-12-20',
          created_at: '2025-08-15',
        },
        {
          class_id: '2',
          name: 'Math 101 - Period 3',
          subject: 'Mathematics',
          grade_level: '9',
          room_number: 'Room 203',
          schedule: [
            { day: 'Monday', start_time: '11:00', end_time: '12:00' },
            { day: 'Wednesday', start_time: '11:00', end_time: '12:00' },
            { day: 'Friday', start_time: '11:00', end_time: '12:00' },
          ],
          student_count: 28,
          max_capacity: 30,
          status: 'active',
          term: 'Fall 2025',
          start_date: '2025-09-01',
          end_date: '2025-12-20',
          created_at: '2025-08-15',
        },
        {
          class_id: '3',
          name: 'Advanced Algebra',
          subject: 'Mathematics',
          grade_level: '10',
          room_number: 'Room 205',
          schedule: [
            { day: 'Tuesday', start_time: '14:00', end_time: '15:30' },
            { day: 'Thursday', start_time: '14:00', end_time: '15:30' },
          ],
          student_count: 18,
          max_capacity: 25,
          status: 'active',
          term: 'Fall 2025',
          start_date: '2025-09-01',
          end_date: '2025-12-20',
          created_at: '2025-08-15',
        },
      ];
      setClasses(mockClasses);
      if (mockClasses.length > 0) {
        setSelectedClass(mockClasses[0]);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadClassStudents = async (classId: string) => {
    if (!token) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/v1/teacher/classes/${classId}/students`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setStudents(response.data.students || []);
      }
    } catch (err) {
      // Mock data
      setStudents([
        {
          student_id: '1',
          name: 'John Doe',
          email: 'john.doe@example.com',
          grade_level: '9',
          enrollment_date: '2025-09-01',
          attendance_percentage: 95,
          grade_average: 85,
          parent_email: 'parent.doe@example.com',
          status: 'active',
        },
        {
          student_id: '2',
          name: 'Jane Smith',
          email: 'jane.smith@example.com',
          grade_level: '9',
          enrollment_date: '2025-09-01',
          attendance_percentage: 98,
          grade_average: 92,
          parent_email: 'parent.smith@example.com',
          status: 'active',
        },
        {
          student_id: '3',
          name: 'Bob Johnson',
          email: 'bob.johnson@example.com',
          grade_level: '9',
          enrollment_date: '2025-09-01',
          attendance_percentage: 90,
          grade_average: 78,
          parent_email: 'parent.johnson@example.com',
          status: 'active',
        },
        {
          student_id: '4',
          name: 'Alice Williams',
          email: 'alice.williams@example.com',
          grade_level: '9',
          enrollment_date: '2025-09-03',
          attendance_percentage: 100,
          grade_average: 88,
          parent_email: 'parent.williams@example.com',
          status: 'active',
        },
        {
          student_id: '5',
          name: 'Charlie Davis',
          email: 'charlie.davis@example.com',
          grade_level: '9',
          enrollment_date: '2025-09-01',
          attendance_percentage: 85,
          grade_average: 75,
          parent_email: 'parent.davis@example.com',
          status: 'active',
        },
      ]);
    }
  };

  const loadAttendance = async (classId: string, date: string) => {
    if (!token) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/v1/teacher/classes/${classId}/attendance`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date },
      });

      if (response.data.success) {
        setAttendance(response.data.attendance || []);
      }
    } catch (err) {
      // Mock data - create attendance records for all students
      const mockAttendance = students.map(student => ({
        attendance_id: `att-${student.student_id}`,
        student_id: student.student_id,
        student_name: student.name,
        date: date,
        status: 'present' as const,
        notes: '',
      }));
      setAttendance(mockAttendance);
    }
  };

  const loadAnnouncements = async (classId: string) => {
    if (!token) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/v1/teacher/classes/${classId}/announcements`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setAnnouncements(response.data.announcements || []);
      }
    } catch (err) {
      // Mock data
      setAnnouncements([
        {
          announcement_id: '1',
          title: 'Chapter 5 Quiz Next Week',
          content: 'We will have our Chapter 5 quiz on Monday. Please review sections 5.1-5.4.',
          type: 'homework',
          created_at: '2025-09-20T10:00:00',
          send_to_parents: true,
        },
        {
          announcement_id: '2',
          title: 'Field Trip Permission Slips',
          content: 'Please return signed permission slips for the Science Museum field trip by Friday.',
          type: 'event',
          created_at: '2025-09-19T14:00:00',
          send_to_parents: true,
        },
      ]);
    }
  };

  const createClass = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API_BASE_URL}/v1/teacher/classes/create`, classForm, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setSuccess('Class created successfully');
        setShowCreateModal(false);
        loadClasses();
      }
    } catch (err) {
      console.error('Failed to create class:', err);
      // For demonstration
      const newClass: Class = {
        class_id: Date.now().toString(),
        name: classForm.name,
        subject: classForm.subject,
        grade_level: classForm.grade_level,
        room_number: classForm.room_number,
        schedule: classForm.schedule,
        student_count: 0,
        max_capacity: classForm.max_capacity,
        status: 'active',
        term: classForm.term,
        start_date: classForm.start_date,
        end_date: classForm.end_date,
        created_at: new Date().toISOString(),
      };
      setClasses([...classes, newClass]);
      setSuccess('Class created successfully (demo mode)');
      setShowCreateModal(false);
    } finally {
      setLoading(false);
    }
  };

  const updateAttendance = async (studentId: string, status: string) => {
    if (!token || !selectedClass) return;

    try {
      await axios.put(
        `${API_BASE_URL}/v1/teacher/classes/${selectedClass.class_id}/attendance`,
        {
          student_id: studentId,
          date: attendanceDate,
          status,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (err) {
      console.error('Failed to update attendance:', err);
    }

    // Update local state
    setAttendance(attendance.map(a => (a.student_id === studentId ? { ...a, status: status as any } : a)));
  };

  const saveAttendance = async () => {
    if (!token || !selectedClass) return;

    setLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/v1/teacher/classes/${selectedClass.class_id}/attendance/save`,
        {
          date: attendanceDate,
          attendance: attendance,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSuccess('Attendance saved successfully');
    } catch (err) {
      setSuccess('Attendance saved successfully (demo mode)');
    } finally {
      setLoading(false);
    }
  };

  const createAnnouncement = async () => {
    if (!token || !selectedClass) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/v1/teacher/classes/${selectedClass.class_id}/announcements`,
        announcementForm,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setSuccess('Announcement created and sent');
        setShowAnnouncementModal(false);
        loadAnnouncements(selectedClass.class_id);
      }
    } catch (err) {
      // For demonstration
      const newAnnouncement: Announcement = {
        announcement_id: Date.now().toString(),
        ...announcementForm,
        created_at: new Date().toISOString(),
      };
      setAnnouncements([newAnnouncement, ...announcements]);
      setSuccess('Announcement created and sent (demo mode)');
      setShowAnnouncementModal(false);
    } finally {
      setLoading(false);
    }
  };

  const removeStudent = async (studentId: string) => {
    if (!token || !selectedClass) return;

    if (!confirm('Are you sure you want to remove this student from the class?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/v1/teacher/classes/${selectedClass.class_id}/students/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudents(students.filter(s => s.student_id !== studentId));
      setSuccess('Student removed from class');
    } catch (err) {
      setStudents(students.filter(s => s.student_id !== studentId));
      setSuccess('Student removed from class (demo mode)');
    }
  };

  const exportRoster = () => {
    const csvContent = [
      ['Student Name', 'Email', 'Grade Level', 'Attendance %', 'Grade Average', 'Parent Email', 'Status'],
      ...students.map(s => [
        s.name,
        s.email,
        s.grade_level,
        `${s.attendance_percentage}%`,
        `${s.grade_average}%`,
        s.parent_email,
        s.status,
      ]),
    ]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `class_roster_${selectedClass?.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    setSuccess('Roster exported successfully');
  };

  const getTodaysSchedule = () => {
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
    return classes
      .flatMap(cls =>
        cls.schedule
          .filter(s => s.day === today)
          .map(s => ({
            ...s,
            className: cls.name,
            room: cls.room_number,
          }))
      )
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  const studentHeaders = [
    { key: 'name', header: 'Name' },
    { key: 'email', header: 'Email' },
    { key: 'grade_level', header: 'Grade' },
    { key: 'attendance', header: 'Attendance' },
    { key: 'average', header: 'Average' },
    { key: 'status', header: 'Status' },
    { key: 'actions', header: 'Actions' },
  ];

  const studentRows = students.map(student => ({
    id: student.student_id,
    name: student.name,
    email: student.email,
    grade_level: `Grade ${student.grade_level}`,
    attendance: `${student.attendance_percentage}%`,
    average: `${student.grade_average}%`,
    status: student.status,
    actions: student,
  }));

  const attendanceHeaders = [
    { key: 'student', header: 'Student' },
    { key: 'present', header: 'Present' },
    { key: 'absent', header: 'Absent' },
    { key: 'late', header: 'Late' },
    { key: 'excused', header: 'Excused' },
  ];

  return (
    <Grid fullWidth>
      <Column span={16}>
        <div
          style={{
            padding: '2rem',
            backgroundColor: 'var(--cds-background)',
            minHeight: '100vh',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <div>
              <h1
                style={{
                  fontSize: '2.5rem',
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  color: 'var(--cds-text-primary)',
                }}
              >
                Class Management
              </h1>
              <p
                style={{
                  fontSize: '1.125rem',
                  color: 'var(--cds-text-secondary)',
                }}
              >
                Organize your classes, manage student rosters, and track attendance.
              </p>
            </div>
            <div>
              <Button
                kind="primary"
                renderIcon={Add}
                onClick={() => setShowCreateModal(true)}
                style={{ marginRight: '0.5rem' }}
              >
                Create New Class
              </Button>
              {selectedClass && (
                <Button kind="secondary" renderIcon={UserMultiple} onClick={() => setShowStudentModal(true)}>
                  Add Students
                </Button>
              )}
            </div>
          </div>

          {error && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error}
              onClose={() => setError(null)}
              style={{ marginBottom: '1rem' }}
            />
          )}

          {success && (
            <InlineNotification
              kind="success"
              title="Success"
              subtitle={success}
              onClose={() => setSuccess(null)}
              style={{ marginBottom: '1rem' }}
            />
          )}

          {loading && <Loading />}

          {/* Class Cards */}
          <Grid style={{ marginBottom: '2rem' }}>
            {classes.map(cls => (
              <Column lg={8} key={cls.class_id} style={{ marginBottom: '1rem' }}>
                <Tile
                  style={{
                    cursor: 'pointer',
                    border: selectedClass?.class_id === cls.class_id ? '2px solid var(--cds-interactive-01)' : 'none',
                  }}
                  onClick={() => setSelectedClass(cls)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                    <UserMultiple size={24} style={{ marginRight: '0.5rem' }} />
                    <h3>{cls.name}</h3>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <Tag type="blue">{cls.student_count} Students</Tag>
                    <Tag type={cls.status === 'active' ? 'green' : 'gray'} style={{ marginLeft: '0.5rem' }}>
                      {cls.status}
                    </Tag>
                    <Tag type="teal" style={{ marginLeft: '0.5rem' }}>
                      {cls.room_number}
                    </Tag>
                  </div>
                  <p style={{ color: 'var(--cds-text-secondary)', fontSize: '0.875rem' }}>
                    {cls.schedule.map(s => `${s.day} ${s.start_time}-${s.end_time}`).join(' | ')}
                  </p>
                  <div style={{ marginTop: '1rem' }}>
                    <ProgressIndicator currentIndex={0} spaceEqually>
                      <ProgressStep
                        label="Capacity"
                        description={`${cls.student_count}/${cls.max_capacity}`}
                        complete={cls.student_count < cls.max_capacity}
                      />
                    </ProgressIndicator>
                  </div>
                </Tile>
              </Column>
            ))}

            {/* Today's Schedule */}
            <Column lg={8}>
              <Tile>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                  <Calendar size={24} style={{ marginRight: '0.5rem' }} />
                  <h3>Today's Schedule</h3>
                </div>
                {getTodaysSchedule().length > 0 ? (
                  <StructuredListWrapper>
                    <StructuredListBody>
                      {getTodaysSchedule().map((schedule, idx) => (
                        <StructuredListRow key={idx}>
                          <StructuredListCell>
                            <strong>
                              {schedule.start_time} - {schedule.end_time}
                            </strong>
                          </StructuredListCell>
                          <StructuredListCell>{schedule.className}</StructuredListCell>
                          <StructuredListCell>{schedule.room}</StructuredListCell>
                        </StructuredListRow>
                      ))}
                    </StructuredListBody>
                  </StructuredListWrapper>
                ) : (
                  <p>No classes scheduled for today</p>
                )}
              </Tile>
            </Column>
          </Grid>

          {/* Selected Class Details */}
          {selectedClass && (
            <Tabs>
              <TabList>
                <Tab>Students</Tab>
                <Tab>Attendance</Tab>
                <Tab>Announcements</Tab>
                <Tab>Class Info</Tab>
              </TabList>
              <TabPanels>
                {/* Students Tab */}
                <TabPanel>
                  <div style={{ marginBottom: '1rem' }}>
                    <Button
                      kind="tertiary"
                      size="sm"
                      renderIcon={DocumentExport}
                      onClick={exportRoster}
                      style={{ marginRight: '0.5rem' }}
                    >
                      Export Roster
                    </Button>
                    <Button
                      kind="tertiary"
                      size="sm"
                      renderIcon={Email}
                      onClick={() => setSuccess('Email sent to all parents')}
                    >
                      Email Parents
                    </Button>
                  </div>
                  <DataTable rows={studentRows} headers={studentHeaders}>
                    {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                      <TableContainer title={`Students in ${selectedClass.name}`}>
                        <Table {...getTableProps()}>
                          <TableHead>
                            <TableRow>
                              {headers.map(header => (
                                <TableHeader {...getHeaderProps({ header })} key={header.key}>
                                  {header.header}
                                </TableHeader>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rows.map(row => (
                              <TableRow {...getRowProps({ row })} key={row.id}>
                                {row.cells.map(cell => {
                                  if (cell.info.header === 'status') {
                                    return (
                                      <TableCell key={cell.id}>
                                        <Tag type={cell.value === 'active' ? 'green' : 'gray'}>{cell.value}</Tag>
                                      </TableCell>
                                    );
                                  }
                                  if (cell.info.header === 'attendance') {
                                    const percentage = parseInt(cell.value);
                                    return (
                                      <TableCell key={cell.id}>
                                        <Tag
                                          type={
                                            percentage >= 95
                                              ? 'green'
                                              : percentage >= 85
                                                ? 'teal'
                                                : percentage >= 75
                                                  ? 'purple'
                                                  : 'red'
                                          }
                                        >
                                          {cell.value}
                                        </Tag>
                                      </TableCell>
                                    );
                                  }
                                  if (cell.info.header === 'average') {
                                    const avg = parseInt(cell.value);
                                    return (
                                      <TableCell key={cell.id}>
                                        <Tag
                                          type={
                                            avg >= 90
                                              ? 'green'
                                              : avg >= 80
                                                ? 'teal'
                                                : avg >= 70
                                                  ? 'blue'
                                                  : avg >= 60
                                                    ? 'purple'
                                                    : 'red'
                                          }
                                        >
                                          {cell.value}
                                        </Tag>
                                      </TableCell>
                                    );
                                  }
                                  if (cell.info.header === 'actions') {
                                    const student = cell.value as Student;
                                    return (
                                      <TableCell key={cell.id}>
                                        <OverflowMenu aria-label="Actions">
                                          <OverflowMenuItem itemText="View Details" onClick={() => {}} />
                                          <OverflowMenuItem
                                            itemText="Contact Parent"
                                            onClick={() => setSuccess(`Email sent to ${student.parent_email}`)}
                                          />
                                          <OverflowMenuItem
                                            itemText="Remove from Class"
                                            isDelete
                                            onClick={() => removeStudent(student.student_id)}
                                          />
                                        </OverflowMenu>
                                      </TableCell>
                                    );
                                  }
                                  return <TableCell key={cell.id}>{cell.value}</TableCell>;
                                })}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </DataTable>
                </TabPanel>

                {/* Attendance Tab */}
                <TabPanel>
                  <div style={{ marginBottom: '2rem' }}>
                    <DatePicker
                      datePickerType="single"
                      onChange={(dates: Date[]) => {
                        if (dates.length > 0) {
                          setAttendanceDate(dates[0].toISOString().split('T')[0]);
                        }
                      }}
                    >
                      <DatePickerInput
                        id="attendance-date"
                        labelText="Attendance Date"
                        placeholder="mm/dd/yyyy"
                        value={attendanceDate}
                      />
                    </DatePicker>
                  </div>

                  <TableContainer title={`Attendance for ${new Date(attendanceDate).toLocaleDateString()}`}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeader>Student</TableHeader>
                          <TableHeader>Status</TableHeader>
                          <TableHeader>Notes</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {attendance.map(record => (
                          <TableRow key={record.attendance_id}>
                            <TableCell>{record.student_name}</TableCell>
                            <TableCell>
                              <Select
                                id={`attendance-${record.student_id}`}
                                value={record.status}
                                onChange={e => updateAttendance(record.student_id, e.target.value)}
                                size="sm"
                                inline
                              >
                                <SelectItem value="present" text="Present" />
                                <SelectItem value="absent" text="Absent" />
                                <SelectItem value="late" text="Late" />
                                <SelectItem value="excused" text="Excused" />
                              </Select>
                            </TableCell>
                            <TableCell>
                              <TextInput
                                id={`notes-${record.student_id}`}
                                value={record.notes}
                                onChange={e => {
                                  setAttendance(
                                    attendance.map(a =>
                                      a.student_id === record.student_id ? { ...a, notes: e.target.value } : a
                                    )
                                  );
                                }}
                                placeholder="Add notes..."
                                size="sm"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <div style={{ marginTop: '1rem' }}>
                    <Button onClick={saveAttendance} kind="primary">
                      Save Attendance
                    </Button>
                  </div>

                  {/* Attendance Summary */}
                  <Grid style={{ marginTop: '2rem' }}>
                    <Column lg={4}>
                      <Tile>
                        <h4>Present</h4>
                        <p style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                          {attendance.filter(a => a.status === 'present').length}
                        </p>
                      </Tile>
                    </Column>
                    <Column lg={4}>
                      <Tile>
                        <h4>Absent</h4>
                        <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--cds-text-error)' }}>
                          {attendance.filter(a => a.status === 'absent').length}
                        </p>
                      </Tile>
                    </Column>
                    <Column lg={4}>
                      <Tile>
                        <h4>Late</h4>
                        <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--cds-support-warning)' }}>
                          {attendance.filter(a => a.status === 'late').length}
                        </p>
                      </Tile>
                    </Column>
                  </Grid>
                </TabPanel>

                {/* Announcements Tab */}
                <TabPanel>
                  <div style={{ marginBottom: '1rem' }}>
                    <Button kind="primary" renderIcon={Add} onClick={() => setShowAnnouncementModal(true)}>
                      Create Announcement
                    </Button>
                  </div>

                  {announcements.map(announcement => (
                    <Tile key={announcement.announcement_id} style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div>
                          <h4>{announcement.title}</h4>
                          <Tag
                            type={
                              announcement.type === 'urgent'
                                ? 'red'
                                : announcement.type === 'event'
                                  ? 'blue'
                                  : announcement.type === 'homework'
                                    ? 'purple'
                                    : 'gray'
                            }
                            style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
                          >
                            {announcement.type}
                          </Tag>
                          {announcement.send_to_parents && (
                            <Tag type="teal" style={{ marginLeft: '0.5rem' }}>
                              Sent to Parents
                            </Tag>
                          )}
                          <p style={{ marginTop: '1rem' }}>{announcement.content}</p>
                          <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)', marginTop: '0.5rem' }}>
                            {new Date(announcement.created_at).toLocaleString()}
                          </p>
                        </div>
                        <OverflowMenu aria-label="Actions">
                          <OverflowMenuItem itemText="Edit" />
                          <OverflowMenuItem itemText="Resend" />
                          <OverflowMenuItem itemText="Delete" isDelete />
                        </OverflowMenu>
                      </div>
                    </Tile>
                  ))}
                </TabPanel>

                {/* Class Info Tab */}
                <TabPanel>
                  <Tile>
                    <h3>Class Information</h3>
                    <StructuredListWrapper style={{ marginTop: '1rem' }}>
                      <StructuredListBody>
                        <StructuredListRow>
                          <StructuredListCell>Name:</StructuredListCell>
                          <StructuredListCell>{selectedClass.name}</StructuredListCell>
                        </StructuredListRow>
                        <StructuredListRow>
                          <StructuredListCell>Subject:</StructuredListCell>
                          <StructuredListCell>{selectedClass.subject}</StructuredListCell>
                        </StructuredListRow>
                        <StructuredListRow>
                          <StructuredListCell>Grade Level:</StructuredListCell>
                          <StructuredListCell>Grade {selectedClass.grade_level}</StructuredListCell>
                        </StructuredListRow>
                        <StructuredListRow>
                          <StructuredListCell>Room:</StructuredListCell>
                          <StructuredListCell>{selectedClass.room_number}</StructuredListCell>
                        </StructuredListRow>
                        <StructuredListRow>
                          <StructuredListCell>Term:</StructuredListCell>
                          <StructuredListCell>{selectedClass.term}</StructuredListCell>
                        </StructuredListRow>
                        <StructuredListRow>
                          <StructuredListCell>Duration:</StructuredListCell>
                          <StructuredListCell>
                            {new Date(selectedClass.start_date).toLocaleDateString()} -{' '}
                            {new Date(selectedClass.end_date).toLocaleDateString()}
                          </StructuredListCell>
                        </StructuredListRow>
                        <StructuredListRow>
                          <StructuredListCell>Enrollment:</StructuredListCell>
                          <StructuredListCell>
                            {selectedClass.student_count} / {selectedClass.max_capacity} students
                          </StructuredListCell>
                        </StructuredListRow>
                        <StructuredListRow>
                          <StructuredListCell>Schedule:</StructuredListCell>
                          <StructuredListCell>
                            {selectedClass.schedule.map(s => `${s.day} ${s.start_time}-${s.end_time}`).join(', ')}
                          </StructuredListCell>
                        </StructuredListRow>
                      </StructuredListBody>
                    </StructuredListWrapper>

                    <div style={{ marginTop: '2rem' }}>
                      <Button kind="secondary" renderIcon={Edit}>
                        Edit Class Details
                      </Button>
                      <Button kind="danger--tertiary" renderIcon={Delete} style={{ marginLeft: '0.5rem' }}>
                        Archive Class
                      </Button>
                    </div>
                  </Tile>
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}

          {/* Create Class Modal */}
          <Modal
            open={showCreateModal}
            onRequestClose={() => setShowCreateModal(false)}
            onRequestSubmit={createClass}
            modalHeading="Create New Class"
            primaryButtonText="Create Class"
            secondaryButtonText="Cancel"
            size="lg"
          >
            <TextInput
              id="class-name"
              labelText="Class Name"
              placeholder="e.g., Math 101 - Period 1"
              value={classForm.name}
              onChange={e => setClassForm({ ...classForm, name: e.target.value })}
              required
            />

            <Grid style={{ marginTop: '1rem' }}>
              <Column lg={8}>
                <Select
                  id="subject"
                  labelText="Subject"
                  value={classForm.subject}
                  onChange={e => setClassForm({ ...classForm, subject: e.target.value })}
                >
                  <SelectItem value="math" text="Mathematics" />
                  <SelectItem value="science" text="Science" />
                  <SelectItem value="english" text="English" />
                  <SelectItem value="history" text="History" />
                  <SelectItem value="art" text="Art" />
                </Select>
              </Column>
              <Column lg={8}>
                <Select
                  id="grade_level"
                  labelText="Grade Level"
                  value={classForm.grade_level}
                  onChange={e => setClassForm({ ...classForm, grade_level: e.target.value })}
                >
                  {[...Array(12)].map((_, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)} text={`Grade ${i + 1}`} />
                  ))}
                </Select>
              </Column>
            </Grid>

            <Grid style={{ marginTop: '1rem' }}>
              <Column lg={8}>
                <TextInput
                  id="room"
                  labelText="Room Number"
                  placeholder="e.g., Room 203"
                  value={classForm.room_number}
                  onChange={e => setClassForm({ ...classForm, room_number: e.target.value })}
                />
              </Column>
              <Column lg={8}>
                <NumberInput
                  id="capacity"
                  label="Maximum Capacity"
                  value={classForm.max_capacity}
                  onChange={(e: any, state: any) => setClassForm({ ...classForm, max_capacity: state.value })}
                  min={1}
                  max={100}
                />
              </Column>
            </Grid>

            <Grid style={{ marginTop: '1rem' }}>
              <Column lg={8}>
                <DatePicker
                  datePickerType="single"
                  onChange={(dates: Date[]) => {
                    if (dates.length > 0) {
                      setClassForm({ ...classForm, start_date: dates[0].toISOString().split('T')[0] });
                    }
                  }}
                >
                  <DatePickerInput id="start_date" labelText="Start Date" placeholder="mm/dd/yyyy" />
                </DatePicker>
              </Column>
              <Column lg={8}>
                <DatePicker
                  datePickerType="single"
                  onChange={(dates: Date[]) => {
                    if (dates.length > 0) {
                      setClassForm({ ...classForm, end_date: dates[0].toISOString().split('T')[0] });
                    }
                  }}
                >
                  <DatePickerInput id="end_date" labelText="End Date" placeholder="mm/dd/yyyy" />
                </DatePicker>
              </Column>
            </Grid>
          </Modal>

          {/* Announcement Modal */}
          <Modal
            open={showAnnouncementModal}
            onRequestClose={() => setShowAnnouncementModal(false)}
            onRequestSubmit={createAnnouncement}
            modalHeading="Create Announcement"
            primaryButtonText="Send Announcement"
            secondaryButtonText="Cancel"
          >
            <TextInput
              id="announcement-title"
              labelText="Title"
              placeholder="Enter announcement title"
              value={announcementForm.title}
              onChange={e => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
              required
            />

            <div style={{ marginTop: '1rem' }}>
              <TextArea
                labelText="Content"
                placeholder="Enter announcement content"
                value={announcementForm.content}
                onChange={e => setAnnouncementForm({ ...announcementForm, content: e.target.value })}
                rows={4}
                required
              />
            </div>

            <div style={{ marginTop: '1rem' }}>
              <Select
                id="announcement-type"
                labelText="Type"
                value={announcementForm.type}
                onChange={e => setAnnouncementForm({ ...announcementForm, type: e.target.value as any })}
              >
                <SelectItem value="general" text="General" />
                <SelectItem value="homework" text="Homework" />
                <SelectItem value="event" text="Event" />
                <SelectItem value="urgent" text="Urgent" />
              </Select>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <Checkbox
                id="send-to-parents"
                labelText="Send notification to parents"
                checked={announcementForm.send_to_parents}
                onChange={(_, { checked }) => setAnnouncementForm({ ...announcementForm, send_to_parents: checked })}
              />
            </div>
          </Modal>
        </div>
      </Column>
    </Grid>
  );
};

export default TeacherClassManagement;
